import { ArrowBack, SaveAlt } from '@mui/icons-material'
import { Badge, createTheme, ThemeProvider } from '@mui/material'
import { Typography, AppBar, IconButton, Toolbar, styled } from 'components/Material'
import bblogo from 'assets/images/bb-logo.png'
import cn from 'classnames'
import { Image } from 'components/Custom/index'
import { MOBILE_BREAKPOINT } from 'utils/responsive'
import { MdAdd, MdClose, MdPersonOutline, MdShoppingCart } from 'react-icons/md'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import colors from '../../../colors'
import { useLocation, useNavigate } from 'react-router-dom'
import useCarrinhoStore from 'store/useCarrinho'
import { Rotas } from 'routes/rotas'
import useSidebarStore from 'store/useSidebar'
import { HandlerErrorMessage } from 'utils/HandlerErrorMessage'
import { useSnackbarAlertContext } from 'contexts/SnackbarContext'

export interface TopAppBarProps {
  route?: string
  leftText?: string
  rightIcon?: 'avatar' | 'cart' | 'add' | 'download' | 'close'
  leftLogo?: boolean
  className?: string
  onRightIconClick?: () => void
  closeSidebar?: () => void
  isSidebarOpen?: boolean
  themeColor?: 'blue' | 'white'
  noBack?: boolean
  onBackClickFunctions?: (() => void)[]
  openBarOnBack?: string
  ariaLabel?: string
  nameButton?: string
  typographyLeftPadding?: string
  title?: string
}

export const TopAppBar = ({
  route,
  leftText,
  leftLogo = false,
  rightIcon,
  onRightIconClick,
  isSidebarOpen = false,
  themeColor = 'blue',
  noBack = true,
  closeSidebar,
  onBackClickFunctions,
  openBarOnBack,
  typographyLeftPadding,
  nameButton,
  ariaLabel,
  className
}: TopAppBarProps) => {
  const { openSidebar } = useSidebarStore((state) => state)

  const screenWidth = window.innerWidth
  const { carrinho_tamanho } = useCarrinhoStore((state) => state)
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert } = useSnackbarAlertContext()

  const isVisible =
    screenWidth > MOBILE_BREAKPOINT || (screenWidth <= MOBILE_BREAKPOINT && !isSidebarOpen)

  const handleNavigation = () => {
    // Executa todas as funções de volta, se forem fornecidas
    if (onBackClickFunctions) {
      onBackClickFunctions.forEach((func) => func())
    }

    // Verifica se deve fechar a barra lateral
    const shouldCloseSidebar = route && route === location.pathname

    if (shouldCloseSidebar) {
      // Fecha a barra lateral se a rota atual for a mesma que a `route`
      if (closeSidebar) {
        closeSidebar()
      }
    } else if (route === '') {
      // Retrocede uma página se a rota for uma string vazia
      navigate(-1)
    } else if (route) {
      // Navega para a rota fornecida se `route` estiver definida
      navigate(route)
    }

    // Abre a barra lateral com um novo contexto se `openBarOnBack` estiver definido
    if (openBarOnBack) {
      openSidebar(openBarOnBack)
    }
  }

  const handleCartClick = () => {
    if (carrinho_tamanho === 0) {
      HandlerErrorMessage({ showAlert, customErrorMessage: 'Carrinho vazio' })
    } else {
      navigate(Rotas.CARRINHO)
    }
  }

  const icons = {
    cart: (
      <Badge
        badgeContent={carrinho_tamanho}
        color="error"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        showZero={false}
      >
        <MdShoppingCart size={24} height={24} color="white" />
      </Badge>
    ),
    avatar: <MdPersonOutline size={24} height={24} color="white" />,
    add: <MdAdd size={24} height={24} color="white" />,
    download: <AssessmentOutlinedIcon />,
    close: (
      <MdClose
        size={24}
        height={24}
        className="text-blue-bb"
        name={nameButton}
        aria-label={ariaLabel}
        onClick={onRightIconClick ?? handleNavigation}
      />
    )
  }

  let IconComponent: JSX.Element | null = null

  if (rightIcon && rightIcon in icons) {
    IconComponent = icons[rightIcon!]
  }

  const theme = createTheme({
    palette: {
      primary: {
        main: themeColor === 'blue' ? colors['blue-bb'] : colors['gray-10']
      }
    }
  })

  return (
    <>
      {isVisible && (
        <ThemeProvider theme={theme}>
          <AppBar
            component="nav"
            position="sticky"
            sx={{
              boxShadow: 'none',
              height: '64px',
              zIndex: 9998,
              borderBottom: '1px solid #CAC4D0'
            }}
            className={className}
          >
            <Toolbar className={cn('topBarContainer')}>
              {leftLogo && (
                <IconButton
                  title="Inicio"
                  className="iconContainer"
                  aria-label="Logo"
                  onClick={() => handleNavigation()}
                >
                  <Image src={bblogo} alt="Logo" className={'w-[24px] h-[24px]'} />
                </IconButton>
              )}
              {route && noBack && !leftLogo && (
                <IconButton title="Voltar" aria-label="Back" onClick={() => handleNavigation()}>
                  <ArrowBack className="text-white-bb" />
                </IconButton>
              )}
              <Typography
                component="div"
                className="text-ellipsis overflow-hidden"
                sx={{
                  flexGrow: 1,
                  fontFamily: 'BB-Font-Bold',
                  fontSize: ' 22px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '28px',
                  margin: '0px 6px',
                  paddingLeft: typographyLeftPadding ?? '0px'
                }}
              >
                {leftText}
              </Typography>
              {rightIcon && (
                <IconButton
                  title={rightIcon === 'avatar' ? 'Perfil' : rightIcon}
                  data-testid="right-icon"
                  className="iconContainer"
                  color="inherit"
                  onClick={() => {
                    if (rightIcon !== 'close' && rightIcon !== 'cart') {
                      onRightIconClick && onRightIconClick()
                    } else if (rightIcon === 'close') {
                      handleNavigation()
                    } else if (rightIcon === 'cart') {
                      handleCartClick()
                    }
                  }}
                >
                  {IconComponent}
                </IconButton>
              )}
            </Toolbar>
          </AppBar>
        </ThemeProvider>
      )}
    </>
  )
}
